
import { HybridServiceAccessor } from "@app/common/hybrid-service-accessor";
import { ILocationInformation } from "@app/interfaces/ILocationInformation";
import { IDriver } from "@interfaces/IDriver";
import { IGeocodedAddress } from "@interfaces/IGeofences";
import { IGoogleMapsMarker, IHasGoogleMapsMarker } from "@interfaces/IGoogleMapsMarkerOptions";
import { IPlot, IVeriMarkerOptions } from "@interfaces/IPlot";
import { IVehicle } from "@interfaces/IVehicle";
import { IAccelerometerRecord } from "@services/accelerometerRecord.service";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
import * as moment from "moment";
import { ReasonCode } from "../enum/ReasonCode";
import { IColour } from "./colour.service";
import { LocationInformationService } from "./location-information/location-information.service";

export interface IGeoLocation {
    Latitude: number;
    Longitude: number;
}

export interface IEventCategory {
    id: number;
    name: string;
    count: number;
    events: IEvent[];
    selected?: boolean;
    color?: IColour;
}

export interface IContextualSpeedingJson {
    EncodedPolyline: string;
    RoadSpeedKph: number;
    MaxSpeedReachedKph: number;
    RoadType: string;
    RoadName: string;
}

export interface IEvent extends IHasGoogleMapsMarker<IEvent> {
    Id: number;
    StartTime: string;
    EndTime: string;
    Category: CommonEventCategory;
    IsException: false;
    StartPlotId: number;
    EndPlotId: number;
    Status: CommonEventStatus;
    Type: ReasonCode;
    DriverId: number;
    VehicleId: number;

    StartLatitude: number;
    EndLatitude: number;
    StartLongitude: number;
    EndLongitude: number;

    StartLocationInformation: ILocationInformation;
    EndLocationInformation: ILocationInformation;
    GeocodedAddress: IGeocodedAddress;

    EventName: string;

    JsonData?: string;

    // ---

    driver?: IDriver;
    vehicle?: IVehicle;

    tagIds?: number[];

    markerModel: IGoogleMapsMarker<IEvent>;
    veriMarkerOptions?: IVeriMarkerOptions;
    color?: IColour;
    eventCategory?: IEventCategory;

    contextualSpeedingData?: IContextualSpeedingJson;
    isEndEvent?: boolean;

    startPlot?: IPlot;
    endPlot?: IPlot;
    duration?: string;

    accelerometerRecord?: IAccelerometerRecord;
}

export enum CommonEventStatus {
    Open,
    Closed
}

export enum CommonEventCategory {
    Maintenance,
    Safety,
    Compliance,
    StatusChange
}

export class EventService {

    private readonly locationInformationService = new HybridServiceAccessor<LocationInformationService>("locationInformationService", this.$injector);


    static $inject = ["$injector", "$http", "$q", "translationService"];

    constructor(
        private readonly $injector: angular.auto.IInjectorService,
        private readonly $http: angular.IHttpService,
        private readonly $q: angular.IQService,
        private readonly translationService: TranslationService,) { }

    getEventsForVehicle(vehicleId: number, startTime: moment.Moment, endTime: moment.Moment, invalidateCache = false): angular.IPromise<IEvent[]> {
        return this.$http({
            url: `/api/commonevents/${vehicleId}`,
            method: "GET",
            cache: true,
            params: {
                start: startTime.toISOString(),
                end: endTime.toISOString(),
                invalidateCache: invalidateCache
            }
        }).then((response: angular.IHttpPromiseCallbackArg<IEvent[]>) => {
            return response.data;
        });
    }

    getEventsForFleet(start: moment.Moment, end: moment.Moment) {
        return this.$http({
            method: "GET",
            url: `/api/commonevents?start=${start.toISOString()}&end=${end.toISOString()}`
        }).then((result: angular.IHttpPromiseCallbackArg<IEvent[]>) => {
            const promises = [];

            result.data.forEach(async event => {
                event.EventName = this.translationService.t("ReasonCodeEnum." + ReasonCode[event.Type]);

                if (event.StartLocationInformation.GeofenceIds?.length) {
                    promises.push(this.locationInformationService.service.getGeofenceNamesString(event.StartLocationInformation).then(geofenceNames => {
                        event.StartLocationInformation.geofenceNames = geofenceNames;
                    }))
                }

                if (event.Type == ReasonCode.ContextualSpeeding) {
                    event.contextualSpeedingData = JSON.parse(event.JsonData);
                }
            });

            return this.$q.all(promises).then(() => {
                result.data.sort((x, y) => {
                    return x.Id - y.Id;
                });

                return result.data;
            })
        });
    }

    getVehicleEventsByType(vehicleId: number, reasonCode: ReasonCode, startTime: moment.Moment, endTime: moment.Moment, invalidateCache = false): angular.IPromise<IEvent[]> {
        //console.warn("getVehicleEventsByType", startTime.format(), endTime.format());
        return this.$http({
            url: `/api/commonevents/${vehicleId}/type/${reasonCode}`,
            method: "GET",
            cache: true,
            params: {
                start: startTime.toISOString(),
                end: endTime.toISOString(),
                invalidateCache: invalidateCache
            }
        }).then((response: angular.IHttpPromiseCallbackArg<IEvent[]>) => {
            return response.data;
        });
    }
}
